import { IMinUserDepricated, IOrgChart, IOrgChartItem } from "@mctypes/global.types";
import {
  Box,
  Button,
  Card,
  Divider,
  FileInput,
  Group,
  Modal,
  SegmentedControl,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { ColDef, GetRowIdParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useState } from "react";
import { read, utils } from "xlsx";
import { AG_GRID_LOCALE_NOR } from "../../../agGridLocale";
import { setShowSuccessMessageThunk } from "../../../global/globalSlice";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { FailedAlert } from "../../../components";
import { setOrgChartInRedux } from "../../actions";
import OrgChart from "../orgChart/OrgChart";
import { convertExcelToOrgChartItems, isOrgChartLevelItemsValid } from "../orgChart/orgChartUtils";
import { doSaveProjectOrgChart } from "./restQueries";
import { formatShortDate } from "./selectors";

type OrgChartGridRow = IOrgChartItem & { _id?: string };
type OrgChartExampleRow = Pick<IOrgChartItem, "level" | "text" | "nameOfUser">;

const OrgChartSettingsPage: React.FC = () => {
  const [mode, setMode] = useState<"TABLE" | "CHART">("TABLE");
  const [importModalOpen, setImportModalOpen] = useState<boolean>(false);
  const project = useAppSelector((state) => state.adminReducer.project);

  const [itemsNotValid, setItemsNotValid] = React.useState<boolean>(false);

  const items = project.orgChart?.items;

  React.useEffect(() => {
    if (items?.length > 0) {
      const _itemsNotValid = isOrgChartLevelItemsValid(items);

      setItemsNotValid(_itemsNotValid === false);
    }
  }, []);

  const columns = React.useMemo<ColDef<OrgChartGridRow>[]>(
    () => [
      {
        field: "_id",
        headerName: "Id",
        hide: true,
      },
      {
        field: "level",
        headerName: "Level",
        filter: "agTextColumnFilter",
        width: 140,
      },
      {
        field: "text",
        headerName: "Text",
        filter: "agTextColumnFilter",
        flex: 1,
        minWidth: 220,
      },
      {
        field: "nameOfUser",
        headerName: "Name of user",
        filter: "agTextColumnFilter",
        flex: 1,
        minWidth: 220,
      },
    ],
    [],
  );

  const defaultColDef = React.useMemo<ColDef<OrgChartGridRow>>(
    () => ({
      sortable: true,
      resizable: true,
      suppressHeaderMenuButton: true,
    }),
    [],
  );

  const rowData: OrgChartGridRow[] = project.orgChart?.items ?? [];

  const getRowId = React.useCallback(
    (params: GetRowIdParams<OrgChartGridRow>) => params.data._id ?? `${params.data.level}-${params.data.text}`,
    [],
  );

  return (
    <Card withBorder p="md">
      {importModalOpen && <ImportOrgChartFromExcel toggle={() => setImportModalOpen(!importModalOpen)} />}
      <Stack gap="sm">
        <Stack gap={4}>
          <Title order={4}>Organsisasjonskart</Title>
          <Text size="sm" c="dimmed">
            Innstillinger som brukes til å bygge kartet
          </Text>
        </Stack>
        <Button onClick={() => setImportModalOpen(true)} variant="outline" size="md" w={"fit-content"}>
          IMPORTER FRA EXCEL
        </Button>
        {project.orgChart.lastUpdated && (project.orgChart.uploadedBy as IMinUserDepricated)?.name != null && (
          <Group gap={4}>
            <Text size="sm">Opplastet {formatShortDate(project.orgChart.lastUpdated)} av</Text>
            <Text size="sm">{(project.orgChart.uploadedBy as IMinUserDepricated)?.name}</Text>
          </Group>
        )}
        <Divider />
        {items?.length > 0 && (
          <>
            {itemsNotValid === true && <FailedAlert title="Inndata er ikke gyldig. Sjekk inndataene og prøv igjen." />}
            <SegmentedControl
              color="mc-primary"
              value={mode}
              onChange={(value) => setMode(value as "TABLE" | "CHART")}
              data={[
                { value: "CHART", label: "KART" },
                { value: "TABLE", label: "Tabell" },
              ]}
            />
            {mode === "TABLE" && (
              <Box className="ag-theme-quartz" h={500} w="100%">
                <AgGridReact<OrgChartGridRow>
                  rowData={rowData}
                  columnDefs={columns}
                  defaultColDef={defaultColDef}
                  getRowId={getRowId}
                  headerHeight={35}
                  animateRows={false}
                  suppressContextMenu={true}
                  localeText={AG_GRID_LOCALE_NOR}
                />
              </Box>
            )}
            {mode === "CHART" && (
              <OrgChart orgChart={project.orgChart} members={project.members} projectId={project._id} />
            )}
          </>
        )}
      </Stack>
    </Card>
  );
};

export default OrgChartSettingsPage;

const ImportOrgChartFromExcel: React.FC<{ toggle: () => void }> = ({ toggle }) => {
  const [failed, setFailed] = useState<boolean>();

  const user = useAppSelector((state) => state.globalReducer.user);
  const dispatch = useAppDispatch();

  const project = useAppSelector((state) => state.adminReducer.project);

  const exampleRows = React.useMemo<OrgChartExampleRow[]>(
    () => [
      { level: "1", text: "Prosjekteringsgruppeleder", nameOfUser: "Hans Ole Haugen" },
      { level: "1.2", text: "Bygg J", nameOfUser: "Bjørnar Foldøy Byberg" },
      { level: "1.2.1", text: "ARK (FK)", nameOfUser: "Hallgeir Halland" },
      { level: "1.2.2", text: "RIBR", nameOfUser: "Bjørn Lundby" },
    ],
    [],
  );

  const exampleColumns = React.useMemo<ColDef<OrgChartExampleRow>[]>(
    () => [
      { field: "level", headerName: "Level", width: 110 },
      { field: "text", headerName: "Text", flex: 1, minWidth: 190 },
      { field: "nameOfUser", headerName: "Name of user", flex: 1, minWidth: 190 },
    ],
    [],
  );

  const exampleDefaultColDef = React.useMemo<ColDef<OrgChartExampleRow>>(
    () => ({
      resizable: true,
      suppressHeaderMenuButton: true,
    }),
    [],
  );

  const handleUpload = (file: File | null) => {
    setFailed(false);

    if (file == null) {
      return;
    }

    const reader = new FileReader();
    reader.onload = function (e) {
      const data = e.target.result;
      let readedData = read(data, { type: "binary" });
      const wsname = readedData.SheetNames[0];
      const ws = readedData.Sheets[wsname];

      /* Convert array to json*/
      const dataParse = utils.sheet_to_json(ws, { header: 1 });
      const orgChartItems = convertExcelToOrgChartItems(dataParse);
      if (orgChartItems == null) {
        setFailed(true);
      } else {
        save(orgChartItems);
      }
    };
    reader.readAsBinaryString(file);
  };

  const save = async (orgChartItems: IOrgChartItem[]) => {
    const orgChart: IOrgChart = {
      items: orgChartItems,
      lastUpdated: new Date(),
      uploadedBy: user._id,
    };
    const updatedOrgChart = await doSaveProjectOrgChart(project._id, orgChart);
    dispatch(setOrgChartInRedux(updatedOrgChart));
    dispatch(setShowSuccessMessageThunk("Organisasjonsskjema importert"));
    toggle();
  };

  return (
    <Modal opened onClose={toggle} title="Importer organisasjonskart fra excel" size="xl">
      <Stack>
        {failed && <FailedAlert title="Opplasting mislyktes. Filene er sannsynligvis ikke i formatet nedenfor" />}
        <Text>Excel-filen skal se slik ut:</Text>
        <Box className="ag-theme-quartz" h={210} w="100%" miw={0}>
          <AgGridReact<OrgChartExampleRow>
            rowData={exampleRows}
            columnDefs={exampleColumns}
            defaultColDef={exampleDefaultColDef}
            headerHeight={35}
            animateRows={false}
            suppressContextMenu={true}
            localeText={AG_GRID_LOCALE_NOR}
          />
        </Box>
        <FileInput
          placeholder="+ Klikk for å legge til fil"
          accept=".xls,.xlsx"
          onChange={handleUpload}
          w="fit-content"
        />
      </Stack>
    </Modal>
  );
};
